// client/src/components/DatabaseManagement.js

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  Card,
  CardContent,
  CardHeader,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Tooltip,
  IconButton,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  TextField,
  FormControl,
  InputLabel,
  Snackbar
} from '@mui/material';
import {
  Backup as BackupIcon,
  Restore as RestoreIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  Storage as StorageIcon,
  Info as InfoIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import axiosInstance from '../axiosInstance';
import AppBarHeader from './AppBarHeader';
import { jwtDecode } from 'jwt-decode';

const SCHEDULE_PRESETS = [
  { value: 'hourly', label: 'Every hour',    intervalHours: 1   },
  { value: '4h',     label: 'Every 4 hours', intervalHours: 4   },
  { value: '12h',    label: 'Every 12 hours',intervalHours: 12  },
  { value: 'daily',  label: 'Daily (2 AM)',  intervalHours: 24  },
  { value: 'weekly', label: 'Weekly (Sun 2 AM)', intervalHours: 168 },
  { value: 'custom', label: 'Custom cron expression', intervalHours: null },
];

const RPO_OPTIONS = [
  { hours: 1,   label: '1 hour — mission critical' },
  { hours: 4,   label: '4 hours — high availability' },
  { hours: 12,  label: '12 hours — standard' },
  { hours: 24,  label: '24 hours — typical' },
  { hours: 48,  label: '48 hours — low priority' },
  { hours: 168, label: '1 week — minimal' },
];

const RTO_OPTIONS = [
  { hours: 1,  label: '< 1 hour — near-zero downtime' },
  { hours: 2,  label: '< 2 hours — minimal downtime' },
  { hours: 4,  label: '< 4 hours — standard' },
  { hours: 8,  label: '< 8 hours — business day' },
  { hours: 24, label: '< 24 hours — next day' },
  { hours: 72, label: '< 72 hours — low priority' },
];

function rpoComplianceChip(schedulePreset, rpoHours) {
  const preset = SCHEDULE_PRESETS.find(p => p.value === schedulePreset);
  if (!preset || preset.intervalHours === null) {
    return <Chip icon={<WarningIcon />} label="Custom — verify manually" color="warning" size="small" />;
  }
  if (preset.intervalHours <= rpoHours) {
    return <Chip icon={<CheckCircleIcon />} label="RPO target met" color="success" size="small" />;
  }
  return <Chip icon={<ErrorIcon />} label="RPO target not met" color="error" size="small" />;
}

function DatabaseManagement() {
  const [backups, setBackups] = useState([]);
  const [dbStatus, setDbStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedBackup, setSelectedBackup] = useState(null);
  const [openRestoreDialog, setOpenRestoreDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [backupInProgress, setBackupInProgress] = useState(false);
  const [restoreInProgress, setRestoreInProgress] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Backup policy state
  const [backupCfg, setBackupCfg] = useState({
    enabled: true, schedulePreset: 'daily', cronCustom: '0 2 * * *',
    retentionDays: 30, minCopies: 3, rpoHours: 24, rtoHours: 4,
  });
  const [cfgSaving, setCfgSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const navigate = useNavigate();
  
  // Get user info from token
  const token = localStorage.getItem('token');
  const user = token ? jwtDecode(token) : null;
  const userId = user ? user.id : null;
  const isAdmin = user ? user.isAdmin : false;

  // Fetch backups, database status, and backup config
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [backupsRes, statusRes, cfgRes] = await Promise.all([
        axiosInstance.get('/admin/database/backups'),
        axiosInstance.get('/admin/database/status'),
        axiosInstance.get('/admin/backup-config'),
      ]);
      setBackups(backupsRes.data.backups || []);
      setDbStatus(statusRes.data.status || null);
      setBackupCfg(cfgRes.data);
    } catch (err) {
      console.error('Error fetching database data:', err);
      setError(err.response?.data?.error || 'Failed to load database information');
      if (err.response?.status === 403) navigate('/dashboard');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const handleCreateBackup = async () => {
    setBackupInProgress(true);
    setError('');
    setSuccess('');
    try {
      const response = await axiosInstance.post('/admin/database/backup');
      setSuccess('Database backup created successfully');
      // Refresh the backup list
      fetchData();
    } catch (err) {
      console.error('Error creating backup:', err);
      setError(err.response?.data?.error || 'Failed to create database backup');
    } finally {
      setBackupInProgress(false);
    }
  };

  const handleOpenRestoreDialog = (backup) => {
    setSelectedBackup(backup);
    setOpenRestoreDialog(true);
  };

  const handleCloseRestoreDialog = () => {
    setOpenRestoreDialog(false);
  };

  const handleRestoreBackup = async () => {
    if (!selectedBackup) return;
    
    setRestoreInProgress(true);
    setError('');
    setSuccess('');
    try {
      const response = await axiosInstance.post('/admin/database/restore', {
        filename: selectedBackup.filename
      });
      setSuccess(`Database restored successfully from ${selectedBackup.filename}`);
      handleCloseRestoreDialog();
      // Refresh the data after restore
      fetchData();
    } catch (err) {
      console.error('Error restoring backup:', err);
      setError(err.response?.data?.error || 'Failed to restore database');
    } finally {
      setRestoreInProgress(false);
    }
  };

  const handleOpenDeleteDialog = (backup) => {
    setSelectedBackup(backup);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const handleDeleteBackup = async () => {
    if (!selectedBackup) return;
    
    setError('');
    setSuccess('');
    try {
      const response = await axiosInstance.delete(`/admin/database/backups/${selectedBackup.filename}`);
      setSuccess(`Backup ${selectedBackup.filename} deleted successfully`);
      handleCloseDeleteDialog();
      // Refresh the backup list
      fetchData();
    } catch (err) {
      console.error('Error deleting backup:', err);
      setError(err.response?.data?.error || 'Failed to delete backup');
    }
  };

  const handleSaveBackupConfig = async () => {
    setCfgSaving(true);
    try {
      await axiosInstance.put('/admin/backup-config', backupCfg);
      setSnackbar({ open: true, message: 'Backup policy saved and schedule reloaded', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.error || 'Failed to save backup policy', severity: 'error' });
    } finally {
      setCfgSaving(false);
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'MMM d, yyyy h:mm a');
    } catch (error) {
      return dateString;
    }
  };

  return (
    <div>
      <AppBarHeader 
        isAdmin={isAdmin} 
        userId={userId} 
        onLogout={handleLogout}
      />
      <Box sx={{ maxWidth: 1200, margin: '0 auto', padding: '20px' }}>
        <Typography variant="h4" sx={{ marginTop: 4, marginBottom: 2, textAlign: 'center' }}>
          Database Management
        </Typography>
        
        <Button
          variant="outlined"
          onClick={() => navigate('/admin')}
          sx={{ mb: 2 }}
        >
          ← Back to Admin Dashboard
        </Button>
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<BackupIcon />}
            onClick={handleCreateBackup}
            disabled={backupInProgress}
          >
            {backupInProgress ? 'Creating Backup...' : 'Create Backup'}
            {backupInProgress && <CircularProgress size={24} sx={{ ml: 1 }} />}
          </Button>
          
          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing...' : 'Refresh'}
            {refreshing && <CircularProgress size={20} sx={{ ml: 1 }} />}
          </Button>
        </Box>
        
        {/* Database Status Section */}
        {dbStatus && (
          <Paper elevation={3} sx={{ p: 2, mb: 4 }}>
            <Typography variant="h5" gutterBottom>
              <StorageIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Database Status
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardHeader title="Database Information" />
                  <CardContent>
                    <Typography><strong>Name:</strong> {dbStatus.database.name}</Typography>
                    <Typography><strong>Collections:</strong> {dbStatus.database.collections}</Typography>
                    <Typography><strong>Total Size:</strong> {dbStatus.database.totalSize}</Typography>
                    <Typography><strong>Storage Size:</strong> {dbStatus.database.storageSize}</Typography>
                    <Typography><strong>Indexes:</strong> {dbStatus.database.indexes}</Typography>
                    <Typography><strong>Index Size:</strong> {dbStatus.database.indexSize}</Typography>
                    <Typography><strong>Objects:</strong> {dbStatus.database.objects?.toLocaleString() || 'N/A'}</Typography>
                  </CardContent>
                </Card>
              </Grid>
              
              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardHeader title="Server Information" />
                  <CardContent>
                    <Typography><strong>Version:</strong> {dbStatus.server.version}</Typography>
                    <Typography><strong>Host:</strong> {dbStatus.server.host || 'N/A'}</Typography>
                    <Typography><strong>Uptime:</strong> {dbStatus.server.uptime}</Typography>
                    <Typography><strong>Connections:</strong> {dbStatus.server.connections}</Typography>
                    <Typography><strong>Max Connections:</strong> {dbStatus.server.maxConnections}</Typography>
                    <Typography><strong>MongoDB Tools:</strong> {dbStatus.server.mongoToolsVersion || 'N/A'}</Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card variant="outlined">
                  <CardHeader 
                    title="Backup Directory" 
                    action={
                      dbStatus.backupDirectory?.error ? (
                        <Tooltip title={dbStatus.backupDirectory.error}>
                          <IconButton size="small" color="error">
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                      ) : null
                    }
                  />
                  <CardContent>
                    <Typography><strong>Path:</strong> {dbStatus.backupDirectory?.path || 'N/A'}</Typography>
                    <Typography>
                      <strong>Status:</strong> {' '}
                      {dbStatus.backupDirectory?.exists ? (
                        <Chip 
                          label="Exists" 
                          color="success" 
                          size="small" 
                          variant="outlined"
                        />
                      ) : (
                        <Chip 
                          label="Missing" 
                          color="error" 
                          size="small" 
                          variant="outlined"
                        />
                      )}
                    </Typography>
                    {dbStatus.backupDirectory?.exists && (
                      <>
                        <Typography>
                          <strong>Writable:</strong> {' '}
                          {dbStatus.backupDirectory?.writable ? (
                            <Chip 
                              label="Yes" 
                              color="success" 
                              size="small" 
                              variant="outlined"
                            />
                          ) : (
                            <Chip 
                              label="No" 
                              color="error" 
                              size="small" 
                              variant="outlined"
                            />
                          )}
                        </Typography>
                        <Typography>
                          <strong>Backup Count:</strong> {dbStatus.backupDirectory?.backupCount || 0}
                        </Typography>
                      </>
                    )}
                    {dbStatus.backupDirectory?.error && (
                      <Typography color="error" sx={{ mt: 1 }}>
                        <strong>Error:</strong> {dbStatus.backupDirectory.error}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
            
            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h6">Collection Details</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Collection</strong></TableCell>
                        <TableCell align="right"><strong>Documents</strong></TableCell>
                        <TableCell align="right"><strong>Size</strong></TableCell>
                        <TableCell align="right"><strong>Storage Size</strong></TableCell>
                        <TableCell align="right"><strong>Avg. Document Size</strong></TableCell>
                        <TableCell align="right"><strong>Indexes</strong></TableCell>
                        <TableCell align="right"><strong>Index Size</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {dbStatus.collections.map((collection) => (
                        <TableRow key={collection.name}>
                          <TableCell>
                            {collection.error ? (
                              <Tooltip title={collection.error}>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Typography color="error">{collection.name}</Typography>
                                  <InfoIcon color="error" fontSize="small" sx={{ ml: 1 }} />
                                </Box>
                              </Tooltip>
                            ) : (
                              collection.name
                            )}
                          </TableCell>
                          <TableCell align="right">{collection.count.toLocaleString()}</TableCell>
                          <TableCell align="right">{collection.size}</TableCell>
                          <TableCell align="right">{collection.storageSize}</TableCell>
                          <TableCell align="right">{collection.avgObjSize}</TableCell>
                          <TableCell align="right">{collection.indexes}</TableCell>
                          <TableCell align="right">{collection.indexSize}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          </Paper>
        )}
        
        {/* Backup Policy Section */}
        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <ScheduleIcon color="primary" />
            <Typography variant="h5">Backup Policy</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure when automated backups run, how long they are retained, and document your recovery objectives.
            Changes take effect immediately without restarting the server.
          </Typography>
          <Divider sx={{ mb: 3 }} />

          <Grid container spacing={3}>
            {/* Enable / Schedule */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>Schedule</Typography>

              <FormControlLabel
                control={
                  <Switch
                    checked={backupCfg.enabled}
                    onChange={e => setBackupCfg(c => ({ ...c, enabled: e.target.checked }))}
                  />
                }
                label="Automated backups enabled"
                sx={{ mb: 2, display: 'block' }}
              />

              <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                <InputLabel>Backup frequency</InputLabel>
                <Select
                  value={backupCfg.schedulePreset}
                  label="Backup frequency"
                  onChange={e => setBackupCfg(c => ({ ...c, schedulePreset: e.target.value }))}
                  disabled={!backupCfg.enabled}
                >
                  {SCHEDULE_PRESETS.map(p => (
                    <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>

              {backupCfg.schedulePreset === 'custom' && (
                <TextField
                  fullWidth
                  size="small"
                  label="Custom cron expression"
                  value={backupCfg.cronCustom}
                  onChange={e => setBackupCfg(c => ({ ...c, cronCustom: e.target.value }))}
                  helperText='e.g. "0 3 * * *" = daily at 3 AM. Uses standard 5-field cron syntax.'
                  sx={{ mb: 2 }}
                  disabled={!backupCfg.enabled}
                />
              )}

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  size="small"
                  label="Retention (days)"
                  type="number"
                  value={backupCfg.retentionDays}
                  onChange={e => setBackupCfg(c => ({ ...c, retentionDays: Number(e.target.value) }))}
                  inputProps={{ min: 1, max: 365 }}
                  helperText="Delete backups older than this"
                  sx={{ flex: 1 }}
                />
                <TextField
                  size="small"
                  label="Min copies"
                  type="number"
                  value={backupCfg.minCopies}
                  onChange={e => setBackupCfg(c => ({ ...c, minCopies: Number(e.target.value) }))}
                  inputProps={{ min: 1, max: 50 }}
                  helperText="Always keep this many backups"
                  sx={{ flex: 1 }}
                />
              </Box>
            </Grid>

            {/* RPO / RTO */}
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>Recovery Objectives</Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                <strong>RPO</strong> (Recovery Point Objective) — maximum acceptable data loss
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                <InputLabel>RPO target</InputLabel>
                <Select
                  value={backupCfg.rpoHours}
                  label="RPO target"
                  onChange={e => setBackupCfg(c => ({ ...c, rpoHours: Number(e.target.value) }))}
                >
                  {RPO_OPTIONS.map(o => (
                    <MenuItem key={o.hours} value={o.hours}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ mb: 2 }}>
                {rpoComplianceChip(backupCfg.schedulePreset, backupCfg.rpoHours)}
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  Current backup frequency: <strong>
                    {SCHEDULE_PRESETS.find(p => p.value === backupCfg.schedulePreset)?.label || backupCfg.schedulePreset}
                  </strong>
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                <strong>RTO</strong> (Recovery Time Objective) — maximum acceptable downtime after an incident
              </Typography>
              <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                <InputLabel>RTO target</InputLabel>
                <Select
                  value={backupCfg.rtoHours}
                  label="RTO target"
                  onChange={e => setBackupCfg(c => ({ ...c, rtoHours: Number(e.target.value) }))}
                >
                  {RTO_OPTIONS.map(o => (
                    <MenuItem key={o.hours} value={o.hours}>{o.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" color="text.secondary">
                For self-hosted deployments, restore time depends on backup size and disk speed.
                Keep your {backupCfg.minCopies} most recent backups readily accessible to meet your RTO.
              </Typography>
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleSaveBackupConfig}
              disabled={cfgSaving}
            >
              {cfgSaving ? 'Saving…' : 'Save Backup Policy'}
            </Button>
          </Box>
        </Paper>

        {/* Backups Section */}
        <Paper elevation={3} sx={{ p: 2 }}>
          <Typography variant="h5" gutterBottom>
            <BackupIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Database Backups
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : backups.length === 0 ? (
            <Alert severity="info">No backups available. Create your first backup using the button above.</Alert>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Filename</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="right">Size</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {backups.map((backup) => (
                    <TableRow key={backup.filename}>
                      <TableCell>
                        <Tooltip title={backup.filename}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <StorageIcon sx={{ mr: 1, color: 'primary.main' }} />
                            <Typography noWrap sx={{ maxWidth: 250 }}>
                              {backup.filename}
                            </Typography>
                          </Box>
                        </Tooltip>
                      </TableCell>
                      <TableCell>{formatDate(backup.created)}</TableCell>
                      <TableCell align="right">{backup.size}</TableCell>
                      <TableCell align="right">
                        <Tooltip title="Restore Database">
                          <IconButton 
                            color="primary"
                            onClick={() => handleOpenRestoreDialog(backup)}
                            disabled={restoreInProgress}
                          >
                            <RestoreIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Backup">
                          <IconButton 
                            color="error"
                            onClick={() => handleOpenDeleteDialog(backup)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
        
        {/* Restore Confirmation Dialog */}
        <Dialog
          open={openRestoreDialog}
          onClose={handleCloseRestoreDialog}
        >
          <DialogTitle>Restore Database</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to restore the database from backup "{selectedBackup?.filename}"? 
              This will replace ALL current data with the data from this backup.
              <br /><br />
              <strong>This action cannot be undone!</strong>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseRestoreDialog} color="primary">
              Cancel
            </Button>
            <Button 
              onClick={handleRestoreBackup} 
              color="error" 
              variant="contained"
              disabled={restoreInProgress}
            >
              {restoreInProgress ? 'Restoring...' : 'Restore'}
              {restoreInProgress && <CircularProgress size={20} sx={{ ml: 1 }} />}
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Delete Confirmation Dialog */}
        <Dialog
          open={openDeleteDialog}
          onClose={handleCloseDeleteDialog}
        >
          <DialogTitle>Delete Backup</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Are you sure you want to delete the backup "{selectedBackup?.filename}"?
              <br /><br />
              <strong>This action cannot be undone!</strong>
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDeleteDialog} color="primary">
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteBackup} 
              color="error" 
              variant="contained"
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        >
          <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </div>
  );
}

export default DatabaseManagement;
